import * as analyticsService from "../AnalyticsService";
import { cameraStorage } from "./CameraStorage";

// Why this module exists
// ----------------------
// `possible_camera_issue` used to be fired from a single heuristic in
// PhotographVehicle: "the customer pressed the same photo slot more than once".
// A plain retake and a dead camera look identical to that heuristic, which is why
// ~43% of sessions carry the event and why it says nothing about the cause.
//
// Photos are captured through a hidden <input type="file" accept="image/*" capture>,
// so the OS camera app owns the failure and there is no error callback to subscribe
// to. What we *can* observe from the page:
//
//   * whether the camera app ever came to the foreground (visibilitychange/pagehide)
//   * whether a file came back at all, and how long the round trip took
//   * whether decode/compress blew up — canvas OOM has a recognisable signature
//   * how bright the returned frame is — some Android camera stacks hand back a
//     black buffer instead of failing
//   * the camera permission state, plus (only when permission is already granted,
//     so we never provoke a permission prompt mid-rental) a getUserMedia probe that
//     separates "denied" from "hardware not obtainable"
//
// Those observations are collapsed into the `reason` parameter now attached to
// every `possible_camera_issue` event.

export type CameraIssueReason =
  | "permission_denied"
  | "hardware_busy"
  | "out_of_memory"
  | "timeout"
  | "dark_frame"
  | "capture_lost"
  | "retake"
  | "unknown";

export type CaptureContext = "vehicle_photo" | "profile_photo";

// "detected" = we observed the failure ourselves.
// "retry_heuristic" = the old press-count trigger fired; this is the subset that
// keeps the pre-change event volume comparable.
export type IssueDetection = "detected" | "retry_heuristic";

export type ProbeState = "granted" | "denied" | "prompt" | "unsupported" | "not_probed";

export interface CameraIssueParams {
  reason: CameraIssueReason;
  detection: IssueDetection;
  camera_context: CaptureContext;
  slot?: string;
  camera_opened?: boolean;
  elapsed_ms?: number;
  press_count?: number;
  probe_state?: ProbeState;
  probe_error?: string;
  mean_luma?: number;
  max_luma?: number;
  image_mp?: number;
  file_kb?: number;
  device_memory_gb?: number;
  browser_name?: string;
  os?: string;
  // Only set when the event is sent before the reservation is in the store (boot),
  // so the event still joins to the booking. Overrides the store-derived value.
  reservation_id?: string;
}

// A frame this dark is not a photo of a car — it is a camera that returned an
// unexposed buffer, or a lens that never opened. Both mean and max are checked so a
// legitimately dim night-time photo (dark overall, but with headlights/streetlights
// in it) is not misfiled as a broken camera.
const DARK_MEAN_LUMA = 18;
const DARK_MAX_LUMA = 64;

// How long the OS camera app gets to reach the foreground before "it never opened"
// becomes the more likely explanation.
const CAMERA_OPEN_GRACE_MS = 2500;

// Backstop so a phone that fails on every single press cannot flood the property.
const MAX_DETECTED_EVENTS_PER_SESSION = 10;

export interface CaptureError extends Error {
  cameraReason?: CameraIssueReason;
  fileKb?: number;
  imageMp?: number;
}

export const captureError = (
  message: string,
  reason: CameraIssueReason,
  meta?: { fileKb?: number; imageMp?: number }
): CaptureError => {
  const err = new Error(message) as CaptureError;
  err.cameraReason = reason;
  err.fileKb = meta?.fileKb;
  err.imageMp = meta?.imageMp;
  return err;
};

export interface CaptureResult {
  meanLuma?: number;
  maxLuma?: number;
  imageMp?: number;
  fileKb?: number;
}

export const isDarkFrame = (meanLuma?: number, maxLuma?: number): boolean =>
  typeof meanLuma === "number" &&
  meanLuma <= DARK_MEAN_LUMA &&
  (typeof maxLuma !== "number" || maxLuma <= DARK_MAX_LUMA);

interface Attempt {
  slot: string;
  context: CaptureContext;
  reservationId?: number;
  startedAt: number;
  cameraOpened: boolean;
}

interface Failure {
  reason: CameraIssueReason;
  slot: string;
  context: CaptureContext;
  elapsedMs: number;
  cameraOpened: boolean;
  meanLuma?: number;
  maxLuma?: number;
  imageMp?: number;
  fileKb?: number;
}

let current: Attempt | null = null;
// The last attempt that neither delivered a file nor threw — the camera app simply
// never came back. Kept separately from `current` because the customer may well
// start a new attempt on top of it.
let abandoned: Attempt | null = null;
let lastFailure: Failure | null = null;
let detectedEventCount = 0;
let listenersBound = false;

// Recovery across a page reload
// -----------------------------
// About half of flagged captures (10–14 Sep 2026: 160 of 337) share one shape: the
// camera was engaged for 10–20 s and no file ever came back. The leading explanation
// on Android is the app process being killed while the camera app is in front —
// react-native-webview keeps its file callback in a plain instance field, so after
// the restart the result has nowhere to go and the page comes back with nothing.
// Nothing in memory survives that. Storage does. So the attempt is written to
// localforage (IndexedDB — it outlives a recreated WebView, where sessionStorage
// may not) before the camera opens, cleared on any resolution, and looked for at
// boot. A marker still there at boot IS a lost capture: the customer gets told,
// and the event goes out as `capture_lost` — the one reason established by direct
// evidence rather than inference from timings.
const IN_FLIGHT_KEY = "cameraCaptureInFlight";
// Older than this and it is a leftover from an abandoned session, not a live loss.
// Cleared silently rather than greeting next week's pickup with a stale warning.
const IN_FLIGHT_STALE_MS = 30 * 60 * 1000;

export interface InFlightMarker {
  slot: string;
  context: CaptureContext;
  reservationId?: number;
  startedAt: number;
  cameraOpened: boolean;
  // Set once the boot check has sent the analytics event, so a second reload
  // before the customer reaches the photo step does not send it again.
  reported?: boolean;
}

const toMarker = (attempt: Attempt): InFlightMarker => ({
  slot: attempt.slot,
  context: attempt.context,
  reservationId: attempt.reservationId,
  startedAt: attempt.startedAt,
  cameraOpened: attempt.cameraOpened,
});

// Storage is diagnostics only: if it is unavailable the capture must still proceed.
// Mutations are chained so they land in call order whatever the driver does — a
// write parked behind a slow driver must never complete after the clear that
// followed it and resurrect a marker for a capture that finished fine.
let storageQueue: Promise<void> = Promise.resolve();
const enqueue = (op: () => Promise<void>): Promise<void> => {
  storageQueue = storageQueue.then(op, op);
  return storageQueue;
};

const writeInFlight = (marker: InFlightMarker) =>
  enqueue(async () => {
    try {
      await cameraStorage.setItem(IN_FLIGHT_KEY, JSON.stringify(marker));
    } catch {}
  });

const clearInFlight = () =>
  enqueue(async () => {
    try {
      await cameraStorage.removeItem(IN_FLIGHT_KEY);
    } catch {}
  });

const readInFlight = async (): Promise<InFlightMarker | null> => {
  try {
    const raw = await cameraStorage.getItem(IN_FLIGHT_KEY);
    if (typeof raw !== "string") return null;
    const marker = JSON.parse(raw) as InFlightMarker;
    return marker && typeof marker.startedAt === "number" ? marker : null;
  } catch {
    return null;
  }
};

const markCameraOpened = () => {
  if (!current) return;
  current.cameraOpened = true;
  // Persisted too: after a reload this is the only record of whether the page saw
  // the camera come up before it died.
  void writeInFlight(toMarker(current));
};

const bindListeners = () => {
  if (listenersBound || typeof document === "undefined") return;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") markCameraOpened();
  });
  window.addEventListener("pagehide", markCameraOpened);
  listenersBound = true;
};

const deviceMemoryGb = (): number | undefined => {
  const mem = (navigator as any)?.deviceMemory;
  return typeof mem === "number" ? mem : undefined;
};

const readPermissionState = async (): Promise<ProbeState> => {
  try {
    const perms: any = (navigator as any)?.permissions;
    if (!perms?.query) return "unsupported";
    const status = await perms.query({ name: "camera" as PermissionName });
    return (status?.state as ProbeState) || "unsupported";
  } catch {
    // Safari/iOS throws for the camera descriptor; Firefox does not implement it.
    return "unsupported";
  }
};

// NotFound/Overconstrained are grouped under hardware_busy: from the customer's side
// they are the same failure — the camera hardware could not be obtained. `probe_error`
// carries the exact DOMException name so the buckets can be split in GA4.
const mapProbeError = (name: string): CameraIssueReason => {
  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
    case "SecurityError":
      return "permission_denied";
    case "NotReadableError":
    case "TrackStartError":
    case "AbortError":
    case "NotFoundError":
    case "DevicesNotFoundError":
    case "OverconstrainedError":
      return "hardware_busy";
    default:
      return "unknown";
  }
};

export const probeCamera = async (): Promise<{
  reason: CameraIssueReason;
  probeState: ProbeState;
  probeError?: string;
}> => {
  const probeState = await readPermissionState();
  if (probeState === "denied") return { reason: "permission_denied", probeState };

  // Only touch getUserMedia when permission is already granted. On "prompt" (or when
  // the Permissions API cannot tell us) a probe would raise the OS permission dialog
  // in the middle of the rental flow — it would break the very capture we are trying
  // to diagnose, and the customer would have no idea what it was asking for.
  if (probeState !== "granted") return { reason: "unknown", probeState };

  const media = navigator?.mediaDevices;
  if (!media?.getUserMedia) {
    return { reason: "unknown", probeState, probeError: "no_media_devices" };
  }
  try {
    const stream = await media.getUserMedia({ video: { facingMode: "environment" } });
    stream.getTracks().forEach((track) => track.stop());
    // The camera opens fine for us, so whatever went wrong is in the OS camera app
    // or the handover back to the page, not in the device.
    return { reason: "unknown", probeState, probeError: "probe_ok" };
  } catch (err: any) {
    const name = String(err?.name || "Error");
    return { reason: mapProbeError(name), probeState, probeError: name };
  }
};

const send = (params: CameraIssueParams) => {
  if (params.detection === "detected") {
    if (detectedEventCount >= MAX_DETECTED_EVENTS_PER_SESSION) return;
    detectedEventCount++;
  }
  const clean: Record<string, string | number | boolean> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) clean[key] = value as any;
  });
  const error = params.probe_error && params.probe_error !== "probe_ok"
    ? params.probe_error
    : undefined;
  analyticsService.logEvent(
    "Possible Camera Issue",
    undefined,
    undefined,
    error,
    clean
  );
};

/** The customer tapped a photo slot; the OS camera app is about to take over. */
export const beginCapture = (
  slot: string,
  context: CaptureContext = "vehicle_photo",
  reservationId?: number
) => {
  bindListeners();
  if (current) abandoned = current;
  current = { slot, context, reservationId, startedAt: Date.now(), cameraOpened: false };
  void writeInFlight(toMarker(current));
};

/**
 * A file came back and was compressed successfully. Returns the reason if the frame
 * itself is unusable (a black photo is a camera failure that the browser reports as
 * a success), otherwise null.
 */
export const completeCapture = (
  slot: string,
  result: CaptureResult = {}
): CameraIssueReason | null => {
  const attempt = current;
  current = null;
  abandoned = null;
  void clearInFlight();

  if (isDarkFrame(result.meanLuma, result.maxLuma)) {
    lastFailure = {
      reason: "dark_frame",
      slot,
      context: attempt?.context || "vehicle_photo",
      elapsedMs: attempt ? Date.now() - attempt.startedAt : 0,
      cameraOpened: attempt?.cameraOpened ?? false,
      ...result,
    };
    send({
      reason: "dark_frame",
      detection: "detected",
      camera_context: lastFailure.context,
      slot,
      camera_opened: lastFailure.cameraOpened,
      elapsed_ms: lastFailure.elapsedMs,
      mean_luma: result.meanLuma,
      max_luma: result.maxLuma,
      image_mp: result.imageMp,
      file_kb: result.fileKb,
      device_memory_gb: deviceMemoryGb(),
    });
    return "dark_frame";
  }

  lastFailure = null;
  return null;
};

/** Decode/compress threw — most often the canvas running out of memory. */
export const failCapture = (slot: string, err: CaptureError | any) => {
  const attempt = current;
  current = null;
  abandoned = null;
  void clearInFlight();

  const reason: CameraIssueReason = err?.cameraReason || "unknown";
  lastFailure = {
    reason,
    slot,
    context: attempt?.context || "vehicle_photo",
    elapsedMs: attempt ? Date.now() - attempt.startedAt : 0,
    cameraOpened: attempt?.cameraOpened ?? false,
    imageMp: err?.imageMp,
    fileKb: err?.fileKb,
  };

  if (reason !== "unknown") {
    send({
      reason,
      detection: "detected",
      camera_context: lastFailure.context,
      slot,
      camera_opened: lastFailure.cameraOpened,
      elapsed_ms: lastFailure.elapsedMs,
      image_mp: err?.imageMp,
      file_kb: err?.fileKb,
      device_memory_gb: deviceMemoryGb(),
      probe_error: String(err?.message || "").slice(0, 100),
    });
  }
};

/** The camera app returned, but with an empty FileList (cancelled, or nothing produced). */
export const captureReturnedNoFile = (slot: string) => {
  const attempt = current;
  current = null;
  abandoned = null;
  void clearInFlight();
  lastFailure = {
    reason: "timeout",
    slot,
    context: attempt?.context || "vehicle_photo",
    elapsedMs: attempt ? Date.now() - attempt.startedAt : 0,
    cameraOpened: attempt?.cameraOpened ?? true,
  };
};

/**
 * Works out why the current/most recent capture is suspect. Called when something
 * already indicates trouble — today that is the press-count heuristic.
 */
export const resolveCameraIssueReason = async (
  fallbackContext: CaptureContext = "vehicle_photo"
): Promise<CameraIssueParams> => {
  const pending = current || abandoned;

  if (pending) {
    const elapsedMs = Date.now() - pending.startedAt;
    const base = {
      detection: "retry_heuristic" as IssueDetection,
      camera_context: pending.context,
      slot: pending.slot,
      camera_opened: pending.cameraOpened,
      elapsed_ms: elapsedMs,
      device_memory_gb: deviceMemoryGb(),
    };

    // The camera app never reached the foreground, so ask the device why.
    if (!pending.cameraOpened && elapsedMs > CAMERA_OPEN_GRACE_MS) {
      const probe = await probeCamera();
      return {
        ...base,
        reason: probe.reason,
        probe_state: probe.probeState,
        probe_error: probe.probeError,
      };
    }

    // The camera did open — it just never handed a file back.
    return { ...base, reason: "timeout", probe_state: "not_probed" };
  }

  if (lastFailure) {
    return {
      reason: lastFailure.reason,
      detection: "retry_heuristic",
      camera_context: lastFailure.context,
      slot: lastFailure.slot,
      camera_opened: lastFailure.cameraOpened,
      elapsed_ms: lastFailure.elapsedMs,
      mean_luma: lastFailure.meanLuma,
      max_luma: lastFailure.maxLuma,
      image_mp: lastFailure.imageMp,
      file_kb: lastFailure.fileKb,
      device_memory_gb: deviceMemoryGb(),
      probe_state: "not_probed",
    };
  }

  // Every capture we saw worked. The customer is simply retaking a photo — this is
  // the bucket the old event could not tell apart from a real fault.
  return {
    reason: "retake",
    detection: "retry_heuristic",
    camera_context: fallbackContext,
    device_memory_gb: deviceMemoryGb(),
    probe_state: "not_probed",
  };
};

/** Fires `possible_camera_issue` for the press-count heuristic, now with a reason. */
export const reportRetryHeuristic = async (opts: {
  context?: CaptureContext;
  slot?: string;
  pressCount?: number;
  browser?: { name?: string; os?: string };
}) => {
  const detail = await resolveCameraIssueReason(opts.context || "vehicle_photo");
  send({
    ...detail,
    slot: detail.slot || opts.slot,
    press_count: opts.pressCount,
    browser_name: opts.browser?.name,
    os: opts.browser?.os,
  });
};

// Resolved once per page load. The photo step awaits this rather than reading the
// same storage key itself, so the two can never race for the marker.
let bootCheck: Promise<InFlightMarker | null> | null = null;
// Set synchronously by the first consumer past its await, so two mounts racing for
// the same marker (StrictMode double-mount, for one) cannot both hand it out.
let lostCaptureClaimed = false;

/**
 * Call once at app boot. If a capture was in flight when the page was last
 * destroyed, sends it as `capture_lost` and seeds the failure state so the next
 * press-count trigger says the same rather than "retake". The marker stays in
 * storage, flagged `reported`, so the photo step can still tell the customer.
 */
export const reportLostCaptureOnBoot = (): Promise<InFlightMarker | null> => {
  if (bootCheck) return bootCheck;
  bootCheck = (async () => {
    const marker = await readInFlight();
    if (!marker) return null;

    const elapsedMs = Date.now() - marker.startedAt;
    if (elapsedMs < 0 || elapsedMs > IN_FLIGHT_STALE_MS) {
      await clearInFlight();
      return null;
    }

    lastFailure = {
      reason: "capture_lost",
      slot: marker.slot,
      context: marker.context,
      elapsedMs,
      cameraOpened: marker.cameraOpened,
    };

    if (!marker.reported) {
      send({
        reason: "capture_lost",
        detection: "detected",
        camera_context: marker.context,
        slot: marker.slot,
        camera_opened: marker.cameraOpened,
        elapsed_ms: elapsedMs,
        device_memory_gb: deviceMemoryGb(),
        reservation_id:
          marker.reservationId != null ? String(marker.reservationId) : undefined,
      });
      marker.reported = true;
      await writeInFlight(marker);
    }
    return marker;
  })();
  return bootCheck;
};

/**
 * The photo step calls this on mount. Hands over the lost capture for this
 * reservation and context — once — and clears it from storage. A marker for a
 * different reservation is left alone; it ages out on its own.
 */
export const consumeLostCapture = async (
  reservationId: number | undefined,
  context: CaptureContext
): Promise<InFlightMarker | null> => {
  const marker = await (bootCheck ?? Promise.resolve(null));
  if (!marker || marker.context !== context) return null;
  if (
    marker.reservationId != null &&
    reservationId != null &&
    marker.reservationId !== reservationId
  ) {
    return null;
  }
  if (lostCaptureClaimed) return null;
  lostCaptureClaimed = true;
  bootCheck = Promise.resolve(null);
  // If the customer has already tapped a slot, the key now holds that newer
  // capture's marker; clearing it would leave *that* capture unrecoverable.
  if (!current) await clearInFlight();
  return marker;
};

/** Test hook — clears the module-level capture state. */
export const resetCameraDiagnostics = () => {
  current = null;
  abandoned = null;
  lastFailure = null;
  detectedEventCount = 0;
  bootCheck = null;
  lostCaptureClaimed = false;
};
